import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CurrentScopeFacade } from '@principle-theorem/ng-principle-shared';
import {
  type IPractice,
  type IVerifoneTerminal,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface IVerifoneTerminalOption {
  practiceRef: DocumentReference<IPractice>;
  terminal: WithRef<IVerifoneTerminal>;
}

export type VerifoneTerminalFormData = Required<
  Pick<IVerifoneTerminal, 'name' | 'uid'>
>;

export interface ISelectedVerifoneTerminal {
  practiceRef: DocumentReference<IPractice>;
  terminalData: VerifoneTerminalFormData;
}

export interface ISelectVerifoneTerminalDialogData {
  terminals: WithRef<IVerifoneTerminal>[];
}

@Component({
  selector: 'pr-select-verifone-terminal',
  templateUrl: './select-verifone-terminal.component.html',
  styleUrls: ['./select-verifone-terminal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectVerifoneTerminalComponent {
  options$: Observable<IVerifoneTerminalOption[]>;

  constructor(
    @Inject(MAT_DIALOG_DATA) data: ISelectVerifoneTerminalDialogData,
    private _dialogRef: MatDialogRef<
      SelectVerifoneTerminalComponent,
      ISelectedVerifoneTerminal
    >,
    private _currentScopeFacade: CurrentScopeFacade
  ) {
    this.options$ = this._currentScopeFacade.currentPractice$
      .pipe(filterUndefined())
      .pipe(
        map((practice) => this._toTerminalOptions(practice.ref, data.terminals))
      );
  }

  select(option: IVerifoneTerminalOption): void {
    this._dialogRef.close(toSelectedVerifoneTerminal(option));
  }

  private _toTerminalOptions(
    practiceRef: DocumentReference<IPractice>,
    terminals: WithRef<IVerifoneTerminal>[]
  ): IVerifoneTerminalOption[] {
    return terminals
      .filter((terminal) => !!terminal.lastPaired)
      .map((terminal) => ({
        terminal,
        practiceRef,
      }));
  }
}

export function toSelectedVerifoneTerminal(
  option: IVerifoneTerminalOption
): ISelectedVerifoneTerminal | undefined {
  if (!option.terminal.lastPaired) {
    return;
  }
  return {
    practiceRef: option.practiceRef,
    terminalData: {
      name: option.terminal.name,
      uid: option.terminal.uid,
    },
  };
}
